import { computed, inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { APP_ROUTES } from '@core/routes';
import { ThemeService } from '@core/services/theme.service';
import { isAuthenticated } from '@features/auth/store/auth.signals';
import { FeatureFlagsPopupService } from '@features/feature-flags/services/feature-flags-popup.service';
import type { SearchCategory } from '@features/search/models/search-options';
import { PnycMenuActionBase } from '@ui-kit/pnyc-menu-button/models/pnyc-menu-action';

@Injectable({ providedIn: 'root' })
export class ToolbarService {
    private readonly router = inject(Router);
    private readonly themeService = inject(ThemeService);
    private readonly featureFlagsPopupService = inject(FeatureFlagsPopupService);

    userMenuItems = computed(() => {
        return isAuthenticated()
            ? this.getUserItems()
            : [];
    });

    toolbarItems = computed(() => {
        return isAuthenticated()
            ? this.getItems()
            : [];
    });

    private getItems(): PnycMenuActionBase[] {
        return [
            {
                text: 'TOOLBAR.TITLES.SALES',
                handler: () => this.onSearch(true, 'SALES')
                // children: [
                //     {
                //         text: 'TOOLBAR.TITLES.SEARCH',
                //         children: this.getSearchChildren()
                //     },
                //     {
                //         text: 'TOOLBAR.TITLES.RESOURCES',
                //         children: this.getResourcesChildren()
                //     },
                // ]
            },
            {
                text: 'TOOLBAR.TITLES.RENTALS',
                handler: () => this.onSearch(false, 'RENTALS')
                // children: [
                //     {
                //         text: 'TOOLBAR.TITLES.SEARCH',
                //         children: this.getSearchChildren(false)
                //     },
                //     {
                //         text: 'TOOLBAR.TITLES.RESOURCES',
                //         children: this.getResourcesChildren(false)
                //     },
                // ]
            },
            {
                text: 'TOOLBAR.TITLES.BUILDINGS',
                handler: () => this.onBuildingSearch()
                // children: [
                //     {
                //         text: 'TOOLBAR.TITLES.SEARCH',
                //         children: [{
                //             text: 'TOOLBAR.TITLES.BUILDING_DIRECTORY',
                //             description: 'TOOLBAR.TITLES.SEARCH_BUILDINGS_CONDOS',
                //             iconName: 'building',
                //         }]
                //     },
                //     {
                //         text: 'TOOLBAR.TITLES.RESOURCES',
                //         children: [{
                //             text: 'TOOLBAR.TITLES.NEIGHBORHOODS_GUIDE',
                //             description: 'TOOLBAR.TITLES.EXPLORE_NYC_NEIGHBORHOODS',
                //             iconName: 'neighborhoods',
                //             handler: () => this.onNeighborhoodGuide()
                //         }]
                //     },
                // ]
            },
            {
                text: 'TOOLBAR.TITLES.ANALYTICS',
                testId: 'menu-analytics',
                handler: () => this.onAnalytics()
            },
            {
                text: 'TOOLBAR.TITLES.NEIGHBORHOODS_GUIDE',
                handler: () => this.onNeighborhoodGuide()
            }
            // {
            //     text: 'TOOLBAR.TITLES.MARKET_ACTIVITY',
            //     handler: () => this.onMarketActivity()
            // }
        ];
    }

    private getSearchChildren(isSales = true): PnycMenuActionBase[] {
        return [
            {
                iconName: 'key',
                text: 'TOOLBAR.TITLES.PROPERTIES',
                description: isSales ? 'TOOLBAR.TITLES.BROWSE_SALE_LISTINGS' : 'TOOLBAR.TITLES.BROWSE_RENTAL_LISTINGS',
                handler: () => this.onSearch(isSales, isSales ? 'SALES' : 'RENTALS')
            },
            {
                iconName: 'image',
                text: 'TOOLBAR.TITLES.VISUAL_LOOKBOOK',
                description: 'TOOLBAR.TITLES.UPLOAD_PHOTOS_FOR_SIMILAR',
                handler: () => this.onImageSearch(isSales, isSales ? 'SALES' : 'RENTALS')
            },
            // {
            //     iconName: 'user-circle',
            //     text: 'TOOLBAR.TITLES.PERSONA_MATCHING',
            //     description: 'TOOLBAR.TITLES.EXPLORE_PSYCHOGRAPHIC_SEARCH_OPTIONS',
            // }
        ];
    }

    private getResourcesChildren(isSales = true): PnycMenuActionBase[] {
        return [{
            iconName: 'files',
            text: 'TOOLBAR.TITLES.COMPARABLES',
            description: isSales ? 'TOOLBAR.TITLES.FIND_SALES_COMPS' : 'TOOLBAR.TITLES.FIND_RENTALS_COMPS',
            handler: () => this.onComparableSeach(isSales ? 'SALES' : 'RENTALS')
        }];
    }

    private getUserItems(): PnycMenuActionBase[] {
        return [
            {
                text: 'TOOLBAR.TITLES.AGENT_HUB',
                type: 'header',
            },
            {
                text: 'TOOLBAR.TITLES.OVERVIEW',
                iconName: 'chart-column',
                testId: 'menu-overview',
                handler: () => this.onAgentHub(),
            },
            {
                text: 'TOOLBAR.TITLES.MY_LISTINGS',
                iconName: 'key',
                handler: () => this.onAgentHub(),
            },
            {
                text: 'TOOLBAR.TITLES.MY_PORTFOLIO',
                iconName: 'folder',
                handler: () => this.onAgentHub(),
            },
            {
                text: 'TOOLBAR.TITLES.ACTIVITY',
                iconName: 'activity',
                handler: () => this.onAgentHub(),
            },
            {
                type: 'divider',
            },
            {
                text: 'TOOLBAR.TITLES.PREFERENCES',
                iconName: 'setting'
            },
            {
                text: 'TOOLBAR.TITLES.ACCOUNT_SETTINGS',
                iconName: 'lock'
            },
            {
                text: 'TOOLBAR.TITLES.KNOWLEDGE_BASE',
                iconName: 'book-open',
                handler: () => this.onMyProfileClicked()
            },
            {
                text: 'TOOLBAR.TITLES.FEATURE_FLAGS',
                iconName: 'sliders',
                handler: () => this.onFeatureFlags()
            },
            {
                text: 'TOOLBAR.TITLES.DESIGN_LAB',
                iconName: 'layout-grid',
                testId: 'menu-design-lab',
                handler: () => this.onDesignLab()
            },
            // {
            //     type: 'divider',
            // },
            // {
            //     type: 'item',
            //     text: isDarkMode() ? 'TOOLBAR.TITLES.LIGHT_MODE' : 'TOOLBAR.TITLES.DARK_MODE',
            //     iconName: isDarkMode() ? 'sun' : 'moon',
            //     handler: () => this.onToggleDarkMode()
            // },
        ];
    }

    private onSearch(isListing: boolean, category?: SearchCategory): void {
        this.router.navigate([APP_ROUTES.SEARCH], { queryParams: { category: category?.toLowerCase() } });
    }

    private onBuildingSearch(): void {
        this.router.navigate([APP_ROUTES.BUILDING_SEARCH]);
    }

    private onImageSearch(isListing: boolean, category?: SearchCategory): void {
        this.router.navigate([APP_ROUTES.IMAGE_SEARCH], { queryParams: { category: category?.toLowerCase() } });
    }

    private onNeighborhoodGuide(): void {
        this.router.navigate([APP_ROUTES.NEIGHBORHOOD_GUIDE]);
    }

    private onAnalytics(): void {
        this.router.navigate([APP_ROUTES.ANALYTICS]);
    }

    private onMarketActivity(): void {
        this.router.navigate([APP_ROUTES.MARKET_ACTIVITY]);
    }

    private onMyProfileClicked(): void {
        this.router.navigate(['/']);
    }

    private onToggleDarkMode() {
        this.themeService.toggleDarkMode();
    }

    private onAgentHub(): void {
        this.router.navigate([APP_ROUTES.AGENT_HUB]);
    }

    private onFeatureFlags(): void {
        this.featureFlagsPopupService.open();
    }

    private onDesignLab(): void {
        this.router.navigate([APP_ROUTES.DESIGN_LAB]);
    }

    private onComparableSeach(category: SearchCategory): void {
        this.router.navigate([APP_ROUTES.CMA_SEARCH], { queryParams: { category: category.toLowerCase() } });
    }
}